import { useCallback, useEffect, useRef, useState } from 'react'
import { getStatsSnapshot, ZERO_WEBRTC_STATS as ZERO_STATS, type WebRTCStats } from '../lib/callStats'
import { captureException } from '../lib/sentry'
import { appendTick, type Tick } from '../lib/tickBuffer'

export type { WebRTCStats }

/** Polling cadence for getStats(). 500ms gives ~60 ticks for a 30s call,
 *  enough for stable p95 percentile (~3rd-largest sample). At 1000ms a
 *  30s call only had 30 ticks — p95 was effectively top-2, too noisy.
 */
const STATS_POLL_INTERVAL_MS = 500

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
]

/**
 * Manages a single WebRTC session for a call.
 * Handles offer/answer, ICE candidates, local mic, and remote audio playback.
 */
export function useWebRTC(
  send: (msg: Record<string, unknown>) => void,
  notify: (msg: string, type: 'error' | 'warning') => void,
) {
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const localStreamRef = useRef<MediaStream | null>(null)
  const audioElRef = useRef<HTMLAudioElement | null>(null)
  const callIdRef = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  // External subscriber notified of remote stream changes. Ref-based
  // (not state) so storing the callback never enters `cleanup`'s deps —
  // see STABILITY INVARIANT block in usePhoneCall.ts (search the marker;
  // line numbers shift with refactors). Signature accepts `null` because
  // `cleanup` notifies subscribers when the stream goes away so they can
  // tear down their own state (e.g. Web Audio graph).
  const onRemoteStreamRef = useRef<((s: MediaStream | null) => void) | null>(null)
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState | null>(null)
  const [stats, setStats] = useState<WebRTCStats>(ZERO_STATS)
  const lastStatsRef = useRef<WebRTCStats>(ZERO_STATS)
  const statsTimer = useRef<ReturnType<typeof setInterval>>(undefined)
  // Per-tick history for v2 quality aggregation. Lives only in memory,
  // never sent to backend as a series — only the aggregates are.
  const tickBufferRef = useRef<Tick[]>([])

  // ⚠️ Empty-deps useCallback is load-bearing. This function ref must stay
  // stable for the life of the hook instance because `cleanup` below lists
  // it as the sole dep, and `cleanup`'s stability propagates into
  // usePhoneCall's defensive effect dep list. Breaking either breaks the
  // chain — see the STABILITY INVARIANT comment in usePhoneCall.ts. If you
  // add deps here, re-read that comment and re-verify each link.
  const stopStatsPolling = useCallback(() => {
    if (statsTimer.current) {
      clearInterval(statsTimer.current)
      statsTimer.current = undefined
    }
    setStats(ZERO_STATS)
    lastStatsRef.current = ZERO_STATS
    tickBufferRef.current = []
  }, [])

  const startStatsPolling = useCallback((pc: RTCPeerConnection) => {
    stopStatsPolling()
    statsTimer.current = setInterval(async () => {
      if (pc.connectionState !== 'connected') return
      const snap = await getStatsSnapshot(pc)
      lastStatsRef.current = snap
      setStats(snap)
      // Tag each tick with monotonic ts at push-time. This `ts` never
      // leaves the browser — only used by aggregateTicks to compute Δt.
      appendTick(tickBufferRef.current, { ...snap, ts: performance.now() })
    }, STATS_POLL_INTERVAL_MS)
  }, [stopStatsPolling])

  /** Synchronous read of the most recent stats tick. Returns ZERO_STATS if no tick yet. */
  const getLastStats = useCallback((): WebRTCStats => lastStatsRef.current, [])

  /** Synchronous read of the full tick history (drained by usePhoneCall on hangup). */
  const getTickBuffer = useCallback((): Tick[] => tickBufferRef.current, [])

  // ⚠️ This useCallback's stable ref is load-bearing for the defensive
  // cleanup effect in usePhoneCall.ts (search STABILITY INVARIANT there).
  // Dep list is intentionally ONLY [stopStatsPolling] — which is itself
  // useCallback([]) above. That makes this ref stable across all renders,
  // which lets usePhoneCall list `webrtcCleanup` in a useEffect dep without
  // re-firing the effect on every render.
  //
  // Do NOT add `send`, `notify`, state setters, or anything that may change
  // ref per render — the body already uses only refs and React setters
  // (setConnectionState is always stable). If you need to reference caller
  // props in here, refactor to read them from a ref.
  //
  // Idempotency: all refs are nulled after use. Safe to call multiple times
  // in a row — usePhoneCall's defensive + unmount effects both fire on real
  // remounts and rely on that property.
  const cleanup = useCallback(() => {
    // Invalidate any in-flight handleOffer
    abortRef.current?.abort()
    abortRef.current = null

    stopStatsPolling()

    localStreamRef.current?.getTracks().forEach(t => t.stop())
    localStreamRef.current = null

    pcRef.current?.close()
    pcRef.current = null

    // Notify subscriber BEFORE detaching the element. The subscriber
    // schedules its own state update (React batched), so its cleanup
    // effect fires later in the commit phase — by then audioElRef.current
    // is already null. Subscribers MUST null-guard the ref.
    onRemoteStreamRef.current?.(null)

    if (audioElRef.current) {
      audioElRef.current.srcObject = null
      audioElRef.current.remove()
      audioElRef.current = null
    }

    callIdRef.current = null
    setConnectionState(null)
  }, [stopStatsPolling])

  // Stable setter for the remote-stream subscriber. Empty deps → ref of
  // setOnRemoteStream is stable for the hook's lifetime, so a consumer
  // useEffect with `[setOnRemoteStream]` dep fires exactly once.
  // ⚠️ DO NOT inline-arrow this in callers — that would re-fire the
  // bridge effect on every render.
  const setOnRemoteStream = useCallback((fn: ((s: MediaStream | null) => void) | null) => {
    onRemoteStreamRef.current = fn
  }, [])

  const handleOffer = useCallback(async (callId: string, sdp: string) => {
    // Clean up any previous session and invalidate any in-flight handleOffer
    if (pcRef.current) cleanup()

    // Create abort signal for this session — cleanup() will abort if called mid-flight
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const { signal } = controller

    callIdRef.current = callId

    // Request microphone — continue without it if denied
    let localStream: MediaStream | null = null
    try {
      localStream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (signal.aborted) {
        localStream.getTracks().forEach(t => t.stop())
        return
      }
      localStreamRef.current = localStream
    } catch (err) {
      if (signal.aborted) return
      if (import.meta.env.DEV) console.warn('[webrtc] getUserMedia failed, continuing without mic:', err)
      // Send to Sentry — operator-side "I can hear but they can't hear me"
      // calls land here in most cases (browser autoplay block, codec issue).
      // captureException is a no-op when Sentry is disabled (lib/sentry.ts
      // contract). Two end-user states are expected, not defects, so skip
      // them to keep Sentry clean:
      //   - NotFoundError    — no audio input device
      //   - NotAllowedError  — permission denied or permission dismissed
      const name = err instanceof Error ? err.name : 'unknown'
      if (name !== 'NotFoundError' && name !== 'NotAllowedError') {
        captureException(
          err instanceof Error ? err : new Error(`getUserMedia failed: ${String(err)}`),
          { phase: 'getUserMedia', errorName: name },
        )
      }
      notify('Microphone access denied — you can hear but not speak', 'warning')
    }

    if (signal.aborted) return

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })
    pcRef.current = pc

    // Add local audio track if available
    if (localStream) {
      for (const track of localStream.getAudioTracks()) {
        pc.addTrack(track, localStream)
      }
    }

    // Track connection state + start stats polling when connected
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState
      if (import.meta.env.DEV) console.log('[webrtc] connection state:', state)
      setConnectionState(state)
      if (state === 'connected') startStatsPolling(pc)
      if (state === 'closed' || state === 'failed') stopStatsPolling()
      if (state === 'failed') {
        notify('Audio connection failed', 'error')
      }
    }
    pc.oniceconnectionstatechange = () => {
      if (import.meta.env.DEV) console.log('[webrtc] ICE state:', pc.iceConnectionState)
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'connected') {
        pc.getStats().then(stats => {
          stats.forEach(s => {
            if (s.type === 'candidate-pair') {
              if (import.meta.env.DEV) console.log('[webrtc] pair:', s.state, s.localCandidateId, s.remoteCandidateId, 'nominated:', s.nominated, 'bytes:', s.bytesReceived, '/', s.bytesSent)
            }
            if (s.type === 'local-candidate' || s.type === 'remote-candidate') {
              if (import.meta.env.DEV) console.log('[webrtc] candidate:', s.type, s.candidateType, s.protocol, s.address, s.port)
            }
          })
        })
      }
    }

    // Play remote audio (call audio from phone). Reuse a single <audio>
    // element across multiple ontrack events (renegotiation / ICE restart):
    // create on first fire, swap srcObject on subsequent fires. Detach
    // happens only in cleanup(). This gives a stable audioElRef.current
    // that downstream consumers (call audio boost) can rely on without
    // racing remove+recreate.
    pc.ontrack = (event) => {
      if (signal.aborted) return
      if (import.meta.env.DEV) console.log('[webrtc] remote track received, streams:', event.streams.length, 'track:', event.track.kind)
      // Use track directly — Pion may not populate event.streams with AddTransceiverFromTrack
      const stream = event.streams[0] ?? new MediaStream([event.track])
      if (audioElRef.current === null) {
        const audio = document.createElement('audio')
        audio.autoplay = true
        audio.srcObject = stream
        document.body.appendChild(audio)
        audio.play().catch(err => {
          // NotAllowedError is the expected browser autoplay block when
          // the operator's tab has no user-interaction yet — skip Sentry
          // (same filter shape as getUserMedia above). AbortError fires
          // when ontrack races with cleanup; also expected.
          const name = err instanceof Error ? err.name : 'unknown'
          if (name !== 'NotAllowedError' && name !== 'AbortError') {
            captureException(
              err instanceof Error ? err : new Error(`audio.play failed: ${String(err)}`),
              { phase: 'audio-play', errorName: name },
            )
          }
          if (import.meta.env.DEV) console.warn('[webrtc] audio play blocked:', err)
        })
        audioElRef.current = audio
      } else {
        audioElRef.current.srcObject = stream
      }
      onRemoteStreamRef.current?.(stream)
    }

    // Set remote offer and create answer
    if (import.meta.env.DEV) console.log('[webrtc] setting remote offer, sdp length:', sdp.length)
    try {
      await pc.setRemoteDescription({ type: 'offer', sdp })
      if (signal.aborted) { pc.close(); return }

      const answer = await pc.createAnswer()
      if (signal.aborted) { pc.close(); return }

      await pc.setLocalDescription(answer)
      if (signal.aborted) { pc.close(); return }

      // Send answer immediately — relay needs our ufrag/pwd to validate STUN checks.
      // Candidates trickle separately via ice_candidate messages.
      if (import.meta.env.DEV) console.log('[webrtc] sending answer, sdp length:', answer.sdp!.length)

      send({
        type: 'webrtc_answer',
        call_id: callId,
        sdp: answer.sdp!,
      })
    } catch (err) {
      if (signal.aborted) return
      // SDP set/create failure — codec mismatch, malformed SDP from relay,
      // or relay state drift. Without captureException these go unobserved
      // in prod and the operator just sees a generic "Call setup failed".
      captureException(
        err instanceof Error ? err : new Error(`sdp-negotiation failed: ${String(err)}`),
        { phase: 'sdp-negotiation' },
      )
      if (import.meta.env.DEV) console.error('[webrtc] SDP negotiation failed:', err)
      notify('Call setup failed — please try again', 'error')
      send({ type: 'hangup', call_id: callId })
      cleanup()
      return
    }

    // Trickle ICE candidates to relay
    pc.onicecandidate = (event) => {
      if (signal.aborted) return
      if (event.candidate) {
        if (import.meta.env.DEV) console.log('[webrtc] trickle candidate:', event.candidate.type, event.candidate.candidate)
        send({
          type: 'ice_candidate',
          call_id: callId,
          candidate: event.candidate.candidate,
        })
      } else {
        if (import.meta.env.DEV) console.log('[webrtc] ICE gathering complete')
      }
    }
  }, [send, notify, cleanup, startStatsPolling, stopStatsPolling])

  const addIceCandidate = useCallback(async (candidate: string) => {
    const pc = pcRef.current
    if (!pc) return
    try {
      await pc.addIceCandidate(new RTCIceCandidate({ candidate, sdpMLineIndex: 0 }))
    } catch (err) {
      // Without captureException the DEV-warn branch dead-code-eliminates
      // in prod build — operator sees a stuck "connecting" call with no
      // signal in Sentry. captureException is no-op when Sentry is
      // disabled (lib/sentry.ts contract).
      //
      // Filter expected teardown-race errors (symmetric to sibling sites
      // at getUserMedia / audio.play / sdp-negotiation):
      //   - InvalidStateError fires when the PC is already closed —
      //     relay forwards trailing ICE candidates after operator hangup,
      //     local `pc` was captured pre-cleanup but the await resolves
      //     against a closed PC. Once-per-call benign noise.
      const name = err instanceof Error ? err.name : 'unknown'
      if (name !== 'InvalidStateError') {
        captureException(
          err instanceof Error ? err : new Error(`addIceCandidate failed: ${String(err)}`),
          { phase: 'addIceCandidate', errorName: name },
        )
      }
      if (import.meta.env.DEV) console.warn('[webrtc] failed to add ICE candidate:', err)
    }
  }, [])

  const setMuted = useCallback((muted: boolean) => {
    const tracks = localStreamRef.current?.getAudioTracks()
    if (tracks) {
      for (const track of tracks) {
        track.enabled = !muted
      }
    }
  }, [])

  // Cleanup stats polling on unmount
  useEffect(() => stopStatsPolling, [stopStatsPolling])

  return {
    handleOffer, setMuted, cleanup, addIceCandidate,
    connectionState, stats, getLastStats, getTickBuffer,
    audioElRef, setOnRemoteStream,
  }
}
